import { Component, HostBinding, OnInit } from '@angular/core';
import { Matter, MatterFileType, MatterClientType } from '@models/matter';
import { LawFirm } from '@models/lawFirms';
import { APSKey } from '@models/aps-key';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { MatterDocumentType, MatterFile } from '@models/matter-file';
import { TestHooks } from './aps.hooks';
import { DocumentAiDataService } from '@services/document-ai/document-ai-data.service';
import { ICON } from '@core/util/icon';
import { filter, map } from 'rxjs/operators';
import { SITE_SECTION } from '@state/navigation/page.model';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import {
	AnalyticsService,
	AnalyticsEvent,
} from '@services/analytics/analytics.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { Position } from './aps-side-form/aps-side-form.component';

@UntilDestroy()
@Component({
	selector: 'app-aps',
	templateUrl: './aps.component.html',
	styleUrls: ['./aps.component.scss'],
	standalone: false,
})
export class ApsComponent implements OnInit {
	testHooks = TestHooks;
	AnalyticsEvent = AnalyticsEvent;
	public buyerClientType = MatterClientType.BUYER;
	public sellerClientType = MatterClientType.SELLER;
	public scrollTo: Position;
	public saleClientTypes = MatterClientType;
	public clientType: MatterClientType;
	public showHeader$ = this.router.events.pipe(
		filter((val) => val instanceof NavigationEnd),
		map((val) => {
			const url = (val as NavigationEnd).url.split('/');
			return url[url.length - 1] === 'review-aps' || false;
		}),
	);
	public sale: Matter | null;
	public fileTypes = MatterFileType;
	public APSFileType = MatterDocumentType.APS;
	public showClerk = true;
	public keys: APSKey[];
	public fileName = 'APS';
	public selectedAPS: MatterFile;
	public isInMatterCreation = false;
	public runMatterDocumentAiScraping = toSignal(
		this.store
			.select(SelectedMatter.runMatterDocumentAiScrapings)
			.pipe(map((x) => x?.[this.matterDocumentType])),
	);

	@HostBinding('class.bg-gray-50') bgGray = false;

	public scrollToFieldName$ = this.documentAiDataService.jumpToSection$().pipe(
		map((section) => {
			const mapping = [
				{ key: 'agreementDate', field: 'agreementDate' },
				{ key: 'Buyer(s)', field: 'buyerNames' }, //  Need to update the key names using that matter object
				{ key: 'Seller(s)', field: 'sellerNames' },
				{ key: 'purchasePrice', field: 'purchasePrice' },
				{ key: 'closingDate', field: 'closingDate' },
				{
					key: 'titleSearchCompletionDate',
					field: 'titleSearchCompletionDate',
				},
			];
			return mapping.find((x) => x.key === section)?.field;
		}),
	);

	public ICON = ICON;

	@ViewSelectSnapshot(SelectedMatter.otherPartyLawFirm) lawFirm: LawFirm;
	@ViewSelectSnapshot(MatterUploadedFilesSelectors.getLatestAPS)
	latestAPS: MatterFile;
	@ViewSelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private analytics: AnalyticsService,
		private documentAiDataService: DocumentAiDataService<unknown>,
		private store: Store,
	) {}

	public matterDocumentType = MatterDocumentType.APS;
	public apsUpload: Record<string, unknown> = {};

	ngOnInit(): void {
		this.route.data.pipe(untilDestroyed(this)).subscribe((routeData) => {
			this.isInMatterCreation =
				routeData.section === SITE_SECTION.MatterCreation;
			this.bgGray = this.isInMatterCreation;
		});
	}

	onFileUploadSuccess(): void {
		if (this.isInMatterCreation) {
			this.analytics.logEvent(
				this.runMatterDocumentAiScraping()
					? AnalyticsEvent.APS_ONBOARDING_AI_ON
					: AnalyticsEvent.APS_ONBOARDING_AI_OFF,
			);
		} else {
			this.analytics.logEvent(
				this.runMatterDocumentAiScraping()
					? AnalyticsEvent.APS_TAB_AI_ON
					: AnalyticsEvent.APS_TAB_AI_OFF,
			);
		}
	}

	jumpToSection = (ev) => {
		// TODO: Implement the jump to section
		//     - Get the page number and the position of the field from the mapping
		//     - Scroll to the page and the position
		//     - Highlight the field

		if (!this.keys) {
			return;
		}

		const pageDetails = this.keys.find((x) => x.key === ev);

		if (!pageDetails) {
			return;
		}

		this.scrollTo = { page: pageDetails.Page - 1, position: pageDetails.Y };
	};
}
